import { addHeader, addFooter } from "../../../utils/Components";
import React, { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../store/hooks";
import { AllComponents } from "../../_default/interfaces/base";
import AddIcon from "@mui/icons-material/Add";

import usePageLanguage from "../../_default/hooks/usePageLanguage";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import { ContentCopy } from "@mui/icons-material";
import StructureAPI from "../../../store/reducers/builderReducers/structure/structureApi";
import DeleteConfirmModal from "../Modal/DeleteConfirmModal";
import TemplateAPI, {
  type Template,
  isTemplateDeleteConflictError,
  parseTemplateDeleteConflictMessage,
} from "../../../store/reducers/builderReducers/templates/templateApi";
import BuilderAlertModal from "../Modal/BuilderAlertModal";
import {
  findTemplateByStoredReference,
  getTemplateSourceComponent,
} from "../../../store/reducers/builderReducers/templates/templateHelpers";
import InfoIcon from "@mui/icons-material/Info";
import { getLegacyWebpageIdForStructure } from "../../../store/reducers/builderReducers/pages/pageIdentity";
import { findPageByWebpageRef } from "../../../store/reducers/builderReducers/pages/pagesApi";
import type { Page } from "../../../store/reducers/builderReducers/pages/pagesTypes";
import {
  findActiveLanguageByCode,
  languageCodesEqual,
} from "../../../utils/languageCode";
import { activeLanguageRowFlagUrl } from "../../_default/icons/languageFlagIcons";

type HeaderFooterListItem = {
  id: string | number;
  name: string;
  languageCode?: string | null;
  template_id?: string | number | null;
  rootComponentUuid?: string | number | null;
};

/**
 * Sidebar-created header/footer templates often use a section row/col scaffold as the
 * embedded root; template metadata uses `use` ("header" | "footer"). Legacy templates
 * may use an actual component root with matching component `type`.
 */
function templateQualifiesForHeaderFooterPicker(
  template: Template,
  source: AllComponents | null,
  typeName: string
): boolean {
  if (source?.type === typeName) return true;
  const use = (template.use ?? "").trim().toLowerCase();
  if (typeName === "header") {
    return use === "header";
  }
  if (typeName === "footer") {
    return use === "footer";
  }
  return false;
}

export default function AddHeaderOrFooter({ typeName }: any) {
  const templates = useAppSelector((state) => state.builder.templates);
  const pages = useAppSelector((state) => state.builder.pages);
  const languages = useAppSelector((state) => state.builder.languageSettings);
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const pagesList = useAppSelector((state) => state.builder.pages.list) as Page[];
  const structureNumericId = useMemo(
    () =>
      getLegacyWebpageIdForStructure(
        pagesList,
        activePageUuid as string | null
      ),
    [pagesList, activePageUuid]
  );
  const webpageRef = structureNumericId ?? activePageUuid;

  const [filteredReusableHeadersMap, setFilteredReusableHeadersMap] = useState<
    any[]
  >([]);

  const [infoVisible, setInfoVisible] = useState(false);

  const [selectedReusableHeaderOption, setSelectedReusableHeaderOption] =
    useState<null | string | number>(null);

  const [headerOptionsOpen, setHeaderOptionsOpen] = useState(false);
  const [headerDetailOptionsOpen, setHeaderDetailOptionsOpen] = useState(false);
  const [headerExistingOptionsOpen, setHeaderExistingOptionsOpen] =
    useState(false);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState<
    null | string | number
  >(null);
  const [templateDeleteConflict, setTemplateDeleteConflict] = useState<{
    message: string;
    details: string[];
  } | null>(null);

  const locale = usePageLanguage();

  const currentLocaleFlagUrl = useMemo(() => {
    const row = findActiveLanguageByCode(
      languages.activeLanguages,
      locale
    );
    return row ? activeLanguageRowFlagUrl(row) : "";
  }, [languages.activeLanguages, locale]);

  const [headerNameValue, setHeaderNameValue] = useState<string>("");

  var onClick = () => addHeader();
  var addComponentFunc = addHeader;
  if (typeName === "footer") {
    onClick = () => addFooter();
    addComponentFunc = addFooter;
  }

  const templateList = templates.toJS() as Template[];

  const getTemplateComponentRef = (template: Template): string | number | null => {
    const source = getTemplateSourceComponent(template);
    if (source?.uuid?.trim()) return source.uuid;
    if (template.rootComponentUuid?.trim()) return template.rootComponentUuid;
    return null;
  };

  const findTemplateByComponentRef = (
    ref: string | number | null | undefined
  ): Template | undefined => {
    if (ref == null) return undefined;
    const wanted = String(ref);
    return templateList.find((template) => {
      const source = getTemplateSourceComponent(template);
      if (source?.uuid?.trim() && source.uuid.trim() === wanted) return true;
      if (
        template.rootComponentUuid?.trim() &&
        template.rootComponentUuid.trim() === wanted
      ) {
        return true;
      }
      return false;
    });
  };

  function resolveInjectSource(row: HeaderFooterListItem): AllComponents | null {
    const tmpl = findTemplateByComponentRef(row.id);
    return getTemplateSourceComponent(tmpl) ?? null;
  }

  useEffect(() => {
    const tplList = templates.toJS() as Template[];

    const items: HeaderFooterListItem[] = tplList
      .map((x) => ({
        template: x,
        source: getTemplateSourceComponent(x),
      }))
      .filter((x) =>
        templateQualifiesForHeaderFooterPicker(x.template, x.source, typeName)
      )
      .map((x) => {
        const componentRef = x.source?.uuid?.trim() || getTemplateComponentRef(x.template);
        const rootUuid =
          x.template.rootComponentUuid?.trim() ||
          (typeof componentRef === "string" ? componentRef : null);
        return {
          name: x.template.name,
          languageCode: x.template.languageCode ?? null,
          id: componentRef as string | number,
          template_id: rootUuid ?? componentRef,
          rootComponentUuid: rootUuid ?? componentRef,
        };
      })
      .filter((x) => x.id != null && x.id !== "")
      .sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

    setFilteredReusableHeadersMap(items);
  }, [templates, typeName]);

  return (
    <>
      <div className={`add-${typeName}`} style={{ cursor: "pointer" }}>
        {/* {typeName === "header" && headerOptionsOpen ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "5px",
                padding: "5px",
              }}
            >
              <div
                className="pages-button"
                style={{
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  fontSize: "12px",
                }}
                onClick={() => {
                  // addHeader();

                  setHeaderDetailOptionsOpen(true);
                }}
              >
                <NoteAddIcon style={{ fontSize: "15px" }} />
                Add new {`${typeName}`}
              </div>
              <div
                className="pages-button"
                style={{
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  fontSize: "12px",
                }}
              >
                <CreateNewFolderIcon style={{ fontSize: "15px" }} />
                Add existing {`${typeName}`}
              </div>
            </div>
          </>
        ) : (
          ""
        )} */}

        {!headerOptionsOpen && (
          <div
            className=""
            style={{
              height: "fit-content",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              fontSize: "12px",
              minWidth: "270px",
              width: typeName === "footer" ? "calc(100% + 12px)" : "auto",
              marginBottom: typeName === "footer" ? "0px" : "4px",
              marginTop: typeName === "footer" ? "0px" : "0px",
              position: typeName === "footer" ? "relative" : "static",
              marginLeft: typeName === "footer" ? "-6px" : "auto",
              marginRight: typeName === "footer" ? "-6px" : "auto",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "5px",
                padding: "0px",
                width: "100%",
                paddingTop: "3px",
                paddingLeft: typeName === "footer" ? "6px" : "0px",
                paddingRight: typeName === "footer" ? "6px" : "0px",
              }}
            >
              <div
                className="pages-button"
                style={{
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  fontSize: "12px",
                }}
                onClick={() => {
                  //addHeader(null);
                  setHeaderExistingOptionsOpen(false);
                  setHeaderDetailOptionsOpen(!headerDetailOptionsOpen);
                }}
              >
                <AddIcon style={{ fontSize: "15px" }} />
                Add {`${typeName}`}
              </div>
              <div
                className="pages-button"
                style={{
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  fontSize: "12px",
                }}
                onClick={() => {
                  setHeaderDetailOptionsOpen(false);
                  setHeaderExistingOptionsOpen(!headerExistingOptionsOpen);
                }}
              >
                <AddToPhotosIcon style={{ fontSize: "15px" }} />
                Select {`${typeName}`}
              </div>
            </div>

            {headerDetailOptionsOpen && !headerExistingOptionsOpen && (
              <div
                style={{
                  width: "100%",
                  padding: "5px 10px",
                  position: typeName === "footer" ? "absolute" : "static",
                  bottom: typeName === "footer" ? "100%" : "auto",
                  left: typeName === "footer" ? "0" : "auto",
                  right: typeName === "footer" ? "0" : "auto",
                  backgroundColor:
                    typeName === "footer" ? "#222222" : "transparent",
                  borderRadius:
                    typeName === "footer" ? "5px 5px 0px 0px" : "0px",
                  marginBottom: typeName === "footer" ? "0px" : "0px",
                  boxShadow:
                    typeName === "footer"
                      ? "0px -2px 8px rgba(0, 0, 0, 0.3)"
                      : "none",
                  zIndex: typeName === "footer" ? 1000 : "auto",
                  overflowY: "visible",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    alignItems: "start",
                    width: "100%",
                    justifyContent: "start",
                  }}
                >
                  <span
                    style={{ fontSize: "12px", textTransform: "capitalize" }}
                  >
                    {typeName} name
                  </span>
                  <input
                    type="text"
                    id="headerNameInput"
                    style={{ width: "100%", fontSize: "15px" }}
                    value={headerNameValue}
                    className="form-input builder-input"
                    placeholder={`Write ${typeName} name ...`}
                    onChange={(e) => {
                      setHeaderNameValue(e.target.value);
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    marginTop: "5px",
                  }}
                >
                  <button
                    className="sidebar-save-btn"
                    onClick={() => {
                      // GEt the input value
                      const headerName = (
                        document.getElementById(
                          "headerNameInput"
                        ) as HTMLInputElement
                      ).value;

                      if (headerName) {
                        addComponentFunc(null, headerName, locale);
                      }
                    }}
                  >
                    <span style={{ textTransform: "capitalize" }}>
                      Add {typeName}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!headerDetailOptionsOpen && headerExistingOptionsOpen && (
              <div
                style={{
                  width: "100%",
                  padding: "5px 10px",
                  position: typeName === "footer" ? "absolute" : "static",
                  bottom: typeName === "footer" ? "100%" : "auto",
                  left: typeName === "footer" ? "0" : "auto",
                  right: typeName === "footer" ? "0" : "auto",
                  backgroundColor:
                    typeName === "footer" ? "#222222" : "transparent",
                  borderRadius:
                    typeName === "footer" ? "5px 5px 0px 0px" : "0px",
                  marginBottom: typeName === "footer" ? "0px" : "0px",
                  boxShadow:
                    typeName === "footer"
                      ? "0px -2px 8px rgba(0, 0, 0, 0.3)"
                      : "none",
                  zIndex: typeName === "footer" ? 1000 : "auto",
                  maxHeight: typeName === "footer" ? "400px" : "auto",
                  overflowY: typeName === "footer" ? "visible" : "visible",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    alignItems: "start",
                    width: "100%",
                    justifyContent: "start",
                  }}
                >
                  <span
                    style={{ fontSize: "12px", textTransform: "capitalize" }}
                  >
                    Select existing {typeName}
                  </span>
                  {filteredReusableHeadersMap?.map((header) => {
                    const flagRow = findActiveLanguageByCode(
                      languages.activeLanguages,
                      header.languageCode
                    );
                    const flag = flagRow
                      ? activeLanguageRowFlagUrl(flagRow)
                      : "";

                    const foundTemplate = findTemplateByStoredReference(
                      templates.toJS() as Template[],
                      header.template_id
                    );

                    const currentPage = pagesList.find(
                      (p) => p.uuid === activePageUuid
                    );
                    const pageLangCode =
                      currentPage?.languageCode ?? locale;
                    /** Only show "new name" cross-language UI when both languages are known and differ. */
                    const needsCrossLanguageHeaderName =
                      header.languageCode != null &&
                      pageLangCode !== "" &&
                      !languageCodesEqual(header.languageCode, pageLangCode);

                    return (
                      <React.Fragment key={header.id}>
                        <div
                          className="singleHeaderOption"
                          style={{
                            justifyContent: "space-between",
                            border: `1px solid ${
                              selectedReusableHeaderOption === header.id
                                ? "#d6d6d6"
                                : "transparent"
                            }`,
                          }}
                          onClick={() => {
                            if (!needsCrossLanguageHeaderName) {
                              const source = resolveInjectSource(header);
                              if (!source || webpageRef == null) return;
                              const sortOrder =
                                typeName === "footer" ? 999 : -1;
                              StructureAPI.injectTemplateComponent(
                                webpageRef,
                                source,
                                {
                                  sort_order: sortOrder,
                                  reuseSourceIdentity: true,
                                  layoutRootKind:
                                    typeName === "footer" ? "footer" : "header",
                                }
                              );
                              setHeaderExistingOptionsOpen(false);
                              setSelectedReusableHeaderOption(null);
                              return;
                            }
                            if (selectedReusableHeaderOption === header.id) {
                              setSelectedReusableHeaderOption(null);
                            } else {
                              setSelectedReusableHeaderOption(header.id);
                              setShowCopyConfirmation(null);
                            }
                          }}
                        >
                          <div>
                            <img
                              src={flag ? flag : ""}
                              style={{
                                width: "20px",
                                height: "auto",
                                marginRight: "5px",
                              }}
                            />
                            {header.name}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "5px",
                              alignItems: "center",
                            }}
                          >
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showCopyConfirmation === header.id) {
                                  setShowCopyConfirmation(null);
                                  return;
                                }

                                setShowCopyConfirmation(header.id);
                                setSelectedReusableHeaderOption(null);
                              }}
                            >
                              <ContentCopy style={{ fontSize: "15px" }} />{" "}
                            </div>
                            <div
                              style={{
                                textAlign: "start",
                                textTransform: "none",
                              }}
                            >
                              {foundTemplate &&
                              foundTemplate.webpages?.map((x) => {
                                const pageName = findPageByWebpageRef(
                                  pages.list,
                                  x
                                )?.name;

                                if (pageName) {
                                  return true;
                                } else {
                                  return false;
                                }
                              })?.length > 0 ? (
                                <DeleteConfirmModal
                                  title={`
                                <div style="margin-top: 5px">
                                This template is used in the following pages: 
                                </div>
                                 <div>
                                 <b> 
                                 ${templates
                                   .find((x) => x.rootComponentUuid === header.rootComponentUuid)
                                   ?.webpages?.map((x) => {
                                     const pageName = findPageByWebpageRef(
                                       pages.list,
                                       x
                                     )?.name;

                                     if (!pageName) return "";

                                     return `<span> - ${pageName}</span>`;
                                   })}
                                 </b>
                                    </div>
                                  
                                <div style="margin-top: 5px">
                                Please remove this ${typeName} from the following pages before deleting it.
                                </div>
                                `}
                                  onConfirm={() => {
                                    const tmpl = findTemplateByStoredReference(
                                      templates.toJS() as Template[],
                                      header.rootComponentUuid
                                    );
                                    if (tmpl) {
                                      TemplateAPI.deleteTemplate(tmpl).catch(
                                        (error: unknown) => {
                                          if (
                                            isTemplateDeleteConflictError(
                                              error
                                            )
                                          ) {
                                            setTemplateDeleteConflict(
                                              parseTemplateDeleteConflictMessage(
                                                error
                                              )
                                            );
                                          }
                                        }
                                      );
                                    }
                                  }}
                                  showActionButton={false}
                                />
                              ) : null}

                              {foundTemplate &&
                                foundTemplate.webpages?.map((x) => {
                                  const pageName = findPageByWebpageRef(
                                    pages.list,
                                    x
                                  )?.name;

                                  if (pageName) {
                                    return true;
                                  } else {
                                    return false;
                                  }
                                })?.length <= 0 && (
                                  <DeleteConfirmModal
                                    title={`Are you sure you want to delete ${header.name}? 
                                <br /> 
                                <div style="margin-top: 5px">
                                Deleting this will remove the ${typeName} from templates. This action cannot be undone.
                                </div>
                                `}
                                    onConfirm={() => {
                                      const tmpl = findTemplateByStoredReference(
                                        templates.toJS() as Template[],
                                        header.rootComponentUuid
                                      );
                                      if (tmpl) {
                                        TemplateAPI.deleteTemplate(tmpl).catch(
                                          (error: unknown) => {
                                            if (
                                              isTemplateDeleteConflictError(
                                                error
                                              )
                                            ) {
                                              setTemplateDeleteConflict(
                                                parseTemplateDeleteConflictMessage(
                                                  error
                                                )
                                              );
                                            }
                                          }
                                        );
                                      }
                                    }}
                                  />
                                )}
                            </div>
                          </div>
                        </div>

                        {selectedReusableHeaderOption === header.id && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "start",
                              alignContent: "center",
                              width: "100%",
                            }}
                          >
                            {selectedReusableHeaderOption === header.id &&
                              needsCrossLanguageHeaderName && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                    alignItems: "start",
                                    justifyContent: "start",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    New {typeName} name{" "}
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        position: "relative",
                                      }}
                                    >
                                      <InfoIcon
                                        onMouseEnter={(e) => {
                                          e.stopPropagation();
                                          setInfoVisible(true);
                                        }}
                                        onMouseLeave={(e) => {
                                          e.stopPropagation();
                                          setInfoVisible(false);
                                        }}
                                      />

                                      {infoVisible && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            top: "20px",
                                            fontSize: "14px",
                                            backgroundColor: "#333",
                                            width: "250px",
                                            padding: "15px",
                                            borderRadius: "3px",
                                            boxShadow:
                                              "0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)",
                                            textTransform: "none",
                                            maxWidth: "300px",
                                            textAlign: "left",
                                            color: "#fcfcfc",
                                          }}
                                        >
                                          You are trying to add another language
                                          {typeName} to your page.
                                          <br /> NB! New {typeName} will get the
                                          current page language.
                                        </span>
                                      )}
                                    </div>
                                  </span>
                                  <div
                                    style={{
                                      width: "100%",
                                      display: "flex",
                                      gap: "5px",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div>
                                      <img
                                        style={{
                                          width: "20px",
                                          height: "auto",
                                          objectFit: "cover",
                                        }}
                                        src={currentLocaleFlagUrl}
                                      />
                                    </div>
                                    <input
                                      type="text"
                                      id="headerNameInput"
                                      style={{
                                        width: "100%",
                                        fontSize: "15px",
                                      }}
                                      value={headerNameValue}
                                      className="form-input builder-input"
                                      placeholder={`Write ${typeName} name ...`}
                                      onChange={(e) => {
                                        setHeaderNameValue(e.target.value);
                                      }}
                                    />
                                  </div>
                                  <button
                                    style={{ marginTop: "5px" }}
                                    className="sidebar-save-btn"
                                    onClick={() => {
                                      const source = resolveInjectSource(header);
                                      if (!source || webpageRef == null)
                                        return;

                                      const sortOrder =
                                        typeName === "footer" ? 999 : -1;

                                      StructureAPI.injectTemplateComponent(
                                        webpageRef,
                                        source,
                                        {
                                          sort_order: sortOrder,
                                          reuseSourceIdentity: true,
                                          layoutRootKind:
                                            typeName === "footer" ? "footer" : "header",
                                        }
                                      );
                                    }}
                                  >
                                    Add {typeName}
                                  </button>{" "}
                                </div>
                              )}

                          </div>
                        )}

                        {showCopyConfirmation === header.id && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                              alignItems: "start",
                              justifyContent: "start",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                textTransform: "capitalize",
                              }}
                            >
                              New {typeName} name{" "}
                              <div
                                style={{
                                  fontSize: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  position: "relative",
                                }}
                              >
                                <InfoIcon
                                  onMouseEnter={(e) => {
                                    e.stopPropagation();
                                    setInfoVisible(true);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.stopPropagation();
                                    setInfoVisible(false);
                                  }}
                                />

                                {infoVisible && (
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: "20px",
                                      fontSize: "14px",
                                      backgroundColor: "#333",
                                      width: "250px",
                                      padding: "15px",
                                      borderRadius: "3px",
                                      boxShadow:
                                        "0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)",
                                      textTransform: "none",
                                      maxWidth: "300px",
                                      textAlign: "left",
                                      color: "#fcfcfc",
                                    }}
                                  >
                                    Creating a copy will duplicate the selected{" "}
                                    {typeName}.
                                    <br /> NB! New {typeName} will get the
                                    current page language.
                                  </span>
                                )}
                              </div>
                            </span>
                            <div
                              style={{
                                width: "100%",
                                display: "flex",
                                gap: "5px",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <img
                                  style={{
                                    width: "20px",
                                    height: "auto",
                                    objectFit: "cover",
                                  }}
                                  src={currentLocaleFlagUrl}
                                />
                              </div>
                              <input
                                type="text"
                                id="headerNameInput"
                                style={{ width: "100%", fontSize: "15px" }}
                                value={headerNameValue}
                                className="form-input builder-input"
                                placeholder={`Write ${typeName} name ...`}
                                onChange={(e) => {
                                  setHeaderNameValue(e.target.value);
                                }}
                              />
                            </div>
                            <button
                              style={{ marginTop: "5px" }}
                              className="sidebar-save-btn"
                              onClick={() => {
                                const source = resolveInjectSource(header);
                                if (!source || webpageRef == null)
                                  return;

                                const sortOrder =
                                  typeName === "footer" ? 999 : -1;

                                StructureAPI.injectTemplateComponent(
                                  webpageRef,
                                  source,
                                  {
                                    sort_order: sortOrder,
                                    reuseSourceIdentity: true,
                                    layoutRootKind:
                                      typeName === "footer" ? "footer" : "header",
                                  }
                                );
                              }}
                            >
                              Create a copy
                            </button>{" "}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <BuilderAlertModal
        open={templateDeleteConflict != null}
        message={templateDeleteConflict?.message ?? ""}
        details={templateDeleteConflict?.details}
        onClose={() => setTemplateDeleteConflict(null)}
      />
    </>
  );
}
